/* eslint-disable import/no-extraneous-dependencies */
import React from "react";
import {
  CircularProgress,
  Box,
  Typography,
  IconButton,
  Tooltip,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import StepContentBox from "../shared/step-content-box";
import { DistinctValue, LabelOption } from "../../types";
import { useNotification } from "../../../../context/notification-provider";
import TileSelector from "./tile-selector";
import EmptyTileState from "./empty-tile-state";
import ValuesContent from "./values-content";
import LinkChartDrawer from "./chart/link-chart-drawer";
import { useDistinctValues } from "./use-distinct-values";

interface DistinctValuesDnDProps {
  values: DistinctValue[];
  labelOptions: LabelOption[];
}

/**
 * DistinctValuesDnD is the main component for managing distinct values
 * with drag & drop categorization. Users select a tile, then can view
 * and categorize values through drag & drop or menu actions.
 */
const DistinctValuesDnD: React.FC<DistinctValuesDnDProps> = () => {
  const { showNotification } = useNotification();
  const {
    selectedTile,
    categories,
    selectedCategory,
    dragOverCategory,
    selectedCategoryLabel,
    allChecked,
    indeterminate,
    mainValues,
    checked,
    selectedItem,
    anchorEl,
    isLoading,
    error,
    fieldId,
    chartDrawerOpen,
    chartItem,
    setSelectedCategory,
    setSelectedTile,
    handleSelectAll,
    handleCheckValue,
    handleOpenMenu,
    handleCloseMenu,
    handleItemClick,
    handleMoveValue,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    getCategoryBackground,
    refreshDistinctValues,
    handleOpenChart,
    handleCloseChart,
  } = useDistinctValues();

  const renderContent = () => {
    if (selectedTile === null) {
      return <EmptyTileState />;
    }

    if (isLoading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: 400,
          }}
        >
          <Typography color="error">
            Error loading distinct values. Please try again.
          </Typography>
        </Box>
      );
    }

    return (
      <ValuesContent
        categories={categories}
        selectedCategory={selectedCategory}
        dragOverCategory={dragOverCategory}
        selectedCategoryLabel={selectedCategoryLabel}
        allChecked={allChecked}
        indeterminate={indeterminate}
        mainValues={mainValues}
        checked={checked}
        selectedItem={selectedItem}
        anchorEl={anchorEl}
        fieldId={fieldId}
        isLoading={isLoading}
        onCategorySelect={setSelectedCategory}
        onSelectAll={handleSelectAll}
        onCheckValue={handleCheckValue}
        onItemClick={handleItemClick}
        onOpenMenu={handleOpenMenu}
        onCloseMenu={handleCloseMenu}
        onMoveValue={handleMoveValue}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        getCategoryBackground={getCategoryBackground}
        refreshDistinctValues={refreshDistinctValues}
        onOpenChart={handleOpenChart}
      />
    );
  };

  return (
    <>
      <StepContentBox title="Distinct Values" contentPadding={0}>
        <Box sx={{ position: "relative" }}>
          <TileSelector
            selectedTile={selectedTile}
            onTileSelect={setSelectedTile}
          />
          {selectedTile !== null && (
            <Tooltip title="Refresh values">
              <IconButton
                onClick={() => {
                  refreshDistinctValues();
                  showNotification({
                    message: "Values refreshed successfully",
                    severity: "success",
                  });
                }}
                disabled={isLoading}
                size="small"
                sx={{
                  position: "absolute",
                  right: 16,
                  top: "50%",
                  transform: "translateY(-50%)",
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
        {renderContent()}
      </StepContentBox>
      <LinkChartDrawer
        open={chartDrawerOpen}
        valueText={chartItem?.value || ""}
        fieldId={fieldId}
        onClose={handleCloseChart}
      />
    </>
  );
};

export default DistinctValuesDnD;
